import { useEffect, useState } from "react";
import { useLazyGetCustomerMandatesV2Query } from "../../../service/customer-service";
import styles from "./AdminCustomerDetailsPage.module.scss";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import CommonDialog from "../../../common/components/dialog/CommonDialog";
import { useChargeAnyAmountMutation, useCheckAccountBalanceMutation } from "../../../service/direct-debit-service";
import { useChargeAnyAmountWithCardMutation } from "../../../service/paystack-service";
const DebitMandate = ({ customerId }: { customerId: string }) => {
	const formatCurrency = (value: number | string): string => {
		const numericValue = typeof value === "string" ? parseFloat(value.replace(/[^0-9.-]+/g, "")) : value;
		return !isNaN(numericValue) ? numericValue.toLocaleString() : "";
	};
	const [showModal, setShowModal] = useState(false);
	const [modalPurpose, setModalPurpose] = useState<"card" | "direct-debit" | "">("");

	const [amount, setAmount] = useState(0);
	const [inputValue, setInputValue] = useState<string>("");
	const [getCustomerMandate, { data, isFetching }] = useLazyGetCustomerMandatesV2Query();
	const [checkBalance, { data: balance, isLoading: isFetchingBalance }] = useCheckAccountBalanceMutation();
	const [directDebitAmount, { isLoading: isLaodingViaWallet }] = useChargeAnyAmountMutation();
	const [chargeWithCard, { isLoading: isChargeWithCardLoading }] = useChargeAnyAmountWithCardMutation();

	const handleGetMandate = () => {
		getCustomerMandate(customerId)
			.unwrap()
			.then()
			.catch(err => toast.error(err?.data?.title as string));
	};

	const formatBalance = (value : number | undefined) =>{
		return value?.toLocaleString() ?? "N/A"
	}

	const handleGetBalance = () => {
		checkBalance({ customerId })
			.unwrap()
			.then()
			.catch(err => toast.error(err?.data?.title as string));
	};

	const handleCharge = () => {
		if (amount <= 0) {
			toast.error("You have to input an amount");
			return;
		}
		if (modalPurpose === "direct-debit" && amount > (balance?.accountBalance ?? 0)) {
			toast.error("Insufficient Balance");
			return;
		}

		if (modalPurpose === "direct-debit") {
			if (data?.directDebitMandateStatus?.toLowerCase() === "unlinked") {
				toast.error("No mandate found");
				return;
			}
			directDebitAmount({ customerId, amount })
				.unwrap()
				.then(() => {
					toast.success("Amount debited successfully");
					setShowModal(false);
					setModalPurpose("");
				})
				.catch(err => toast.error(err?.data?.title as string));
		}

		if (modalPurpose === "card") {
			if (data?.cardPaymentStatus?.toLowerCase() === "unlinked") {
				toast.error("No card linked");
				return;
			}
			chargeWithCard({ customerId, amount })
				.unwrap()
				.then(() => {
					toast.success("Amount charged successfully");
					setShowModal(false);
					setModalPurpose("");
				})
				.catch(err => toast.error(err?.data?.title as string));
		}
	};

	useEffect(() => {
		handleGetMandate();
		handleGetBalance();
	}, []);

	const unformatCurrency = (value: string): number => {
		return parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formattedValue = formatCurrency(e.target.value);
		setInputValue(formattedValue);
	};

	const handleBlur = () => {
		const numericValue = unformatCurrency(inputValue);
		setInputValue(formatCurrency(numericValue));
		setAmount(numericValue);
	};

	const handleShowModal = (purpose: "card" | "direct-debit" | "") => {
		setShowModal(true);
		setModalPurpose(purpose);
	};

	return (
		<div className={styles.paymentTables}>
			{showModal && (
				<CommonDialog noPadding>
					<div className={styles.chargeModalHeader}>
						<h3>Debit Amount</h3>
					</div>
					<div className={styles.chargeModalBody}>
						<div className={styles.chargeInput}>
							<label>Enter Amount</label>
							<input value={inputValue} onChange={handleChange} onBlur={handleBlur} required placeholder="e.g 120,000" />
						</div>
					</div>
					<div className={styles.footer}>
						<button disabled={isChargeWithCardLoading || isLaodingViaWallet} onClick={() => setShowModal(!showModal)}>
							Cancel
						</button>
						<button onClick={handleCharge} disabled={isChargeWithCardLoading || isLaodingViaWallet}>
							{isChargeWithCardLoading || isLaodingViaWallet ? "Processing.." : "Confirm"}{" "}
						</button>
					</div>
				</CommonDialog>
			)}
			<div className={styles.table}>
				<div className={styles.tableHeader}>
					<span>Card Payment</span>
					<span className={styles.status}>{isFetching ? <BounceLoader color="orange" size={10} /> : data?.cardPaymentStatus}</span>
				</div>
				<div className={styles.tableRow}>
					<span className={styles.label}>Debit Card</span>
					<button type="button" onClick={() => handleShowModal("card")} className={styles.actionButton}>
						Authorize Debit
					</button>
				</div>
			</div>
			<div className={styles.table} style={{ width: 500 }}>
				<div className={styles.tableHeader}>
					<span>Direct Debit Mandate</span>
					<span className={styles.status}>{isFetching ? <BounceLoader color="orange" size={10} /> : data?.directDebitMandateStatus}</span>
				</div>
				<div className={styles.tableRow}>
					<span className={styles.label}>Check Balance</span>
					<span className={styles.value}>{isFetchingBalance ? <BounceLoader color="orange" size={10} /> : formatBalance(balance?.accountBalance)}</span>
					<button onClick={handleGetBalance} disabled={isFetchingBalance} type="button" className={styles.actionButton}>
						Get Balance
					</button>
				</div>
				<div className={styles.tableRow}>
					<span className={styles.label}>Debit Account</span>
					<button className={styles.actionButton} type="button" onClick={() => handleShowModal("card")}>
						Debit Account
					</button>
				</div>
			</div>
		</div>
	);
};
export default DebitMandate;
